import { extractErrorDetails } from '../utils/extract-error-message';

/**
 * 429 Too Many Requests: el backend rechaza un reenvío de código por correo
 * porque el anterior sigue dentro de la ventana de enfriamiento.
 */
export const RESEND_COOLDOWN_STATUS = 429;

/**
 * Clave que emite `ms-documents` en el 429 del reenvío. Se compara por sufijo
 * porque el gateway a veces reenvía la clave con prefijo de módulo.
 */
export const RESEND_COOLDOWN_MESSAGE_KEY = 'EMAIL_CODE_RESEND_COOLDOWN';

/**
 * Ventana de enfriamiento del backend hoy: 60s por (documento, email).
 * Se usa solo como respaldo cuando la respuesta no trae `retryAfter`.
 */
export const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

/** Techo defensivo: un `retryAfter` absurdo no debe congelar el modal. */
const MAX_RESEND_COOLDOWN_SECONDS = 15 * 60;

type TUnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): TUnknownRecord | undefined =>
  value && typeof value === 'object' ? (value as TUnknownRecord) : undefined;

/**
 * Claves bajo las que el cuerpo útil del error puede quedar anidado.
 *
 * - `data` / `response`: `httpClient.handleError` lanza `{ data, status, message }`,
 *   el `AxiosError` crudo lo deja en `response.data` y algunos envoltorios lo
 *   anidan una vez más en `data.data`.
 * - `details` / `innerError`: `CustomRpcException` de los microservicios mete lo
 *   suyo en `details`, y el payload que arma envuelve el detalle propio del
 *   llamador en `details.details`. El cooldown de reenvío llega justamente ahí
 *   (`signing-email-code.service.ts:182-188`), así que sin bajar a estas capas
 *   `retryAfterSeconds` nunca se lee.
 * - `error`: el gateway a veces reenvía el error original bajo esa clave.
 */
const NESTED_PAYLOAD_KEYS = ['data', 'response', 'details', 'innerError', 'error'] as const;

/** Tope de recorrido: suficiente para las formas conocidas, sin volverse un walk ciego. */
const MAX_PAYLOAD_DEPTH = 6;

/**
 * Capas donde puede venir el cuerpo del error según por dónde pasó. Recorre en
 * anchura para que las capas más superficiales ganen: si el mismo campo viene
 * arriba y anidado, manda el de arriba.
 */
const collectPayloads = (error: unknown): TUnknownRecord[] => {
  const root = asRecord(error);
  if (!root) return [];

  const collected: TUnknownRecord[] = [];
  const seen = new Set<TUnknownRecord>([root]);
  let frontier: TUnknownRecord[] = [root];

  for (let depth = 0; depth <= MAX_PAYLOAD_DEPTH && frontier.length > 0; depth += 1) {
    collected.push(...frontier);

    const next: TUnknownRecord[] = [];
    for (const layer of frontier) {
      for (const key of NESTED_PAYLOAD_KEYS) {
        const child = asRecord(layer[key]);
        if (child && !seen.has(child)) {
          seen.add(child);
          next.push(child);
        }
      }
    }
    frontier = next;
  }

  return collected;
};

const readStatus = (error: unknown): number | undefined => {
  for (const payload of collectPayloads(error)) {
    const status = payload.status ?? payload.statusCode;
    if (typeof status === 'number') return status;
  }
  return undefined;
};

const messageIncludesCooldownKey = (value: unknown): boolean => {
  const { messages, codes } = extractErrorDetails(value);
  return [...messages, ...codes].some((entry) => entry.includes(RESEND_COOLDOWN_MESSAGE_KEY));
};

/**
 * ¿El error trae la clave de cooldown que emite `ms-documents`?
 *
 * Es el reconocimiento estricto, para quien ve tráfico de toda la app (el
 * interceptor del httpClient) y no puede asumir que cualquier 429 sea este.
 */
export const hasResendCooldownMessage = (error: unknown): boolean =>
  collectPayloads(error).some((payload) => messageIncludesCooldownKey(payload.message));

/**
 * Reconoce el 429 de cooldown del reenvío.
 *
 * Tolerante a la forma: alcanza con el status o con la clave del backend en el
 * mensaje, porque el mismo error llega distinto según la capa que lo relance.
 * Solo lo usa el modal 2FA, donde el único pedido que puede volver 429 es el
 * reenvío del código, así que el status por sí solo ya es concluyente.
 */
export const isResendCooldownError = (error: unknown): boolean =>
  readStatus(error) === RESEND_COOLDOWN_STATUS || hasResendCooldownMessage(error);

/**
 * Normaliza cualquier representación razonable de segundos a un entero válido.
 * `Retry-After` HTTP viaja como string, y el backend puede mandarlo numérico.
 */
const toSeconds = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.trim()) : NaN;

  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;

  return Math.min(Math.ceil(parsed), MAX_RESEND_COOLDOWN_SECONDS);
};

/**
 * Nombres que puede tener el campo según quién serializa la respuesta.
 * `ms-documents` lo manda como `retryAfterSeconds`; el resto son variantes que
 * ya aparecen en otros payloads del gateway y en el header estándar.
 */
const RETRY_AFTER_FIELDS = ['retryAfter', 'retry_after', 'retryAfterSeconds', 'retryAfterSec'] as const;

const readRetryAfterFromHeaders = (error: unknown): number | undefined => {
  for (const payload of collectPayloads(error)) {
    const headers = asRecord(payload.headers);
    if (!headers) continue;

    const raw = headers['retry-after'] ?? headers['Retry-After'];
    const seconds = toSeconds(raw);
    if (seconds !== undefined) return seconds;
  }
  return undefined;
};

/**
 * Segundos que faltan para poder reenviar.
 *
 * Contrato real del 429 de `ms-documents` (`signing-email-code.service.ts:182-188`):
 * el valor viaja dentro de `details`, que `CustomRpcException` anida una vez más
 * bajo su propio `details`.
 *
 * ```json
 * {
 *   "status": 429,
 *   "message": "DOCUMENTS.USECASES.EMAIL_CODE_RESEND_COOLDOWN",
 *   "details": { "details": { "retryAfterSeconds": 42, "nextAllowedAt": "..." } }
 * }
 * ```
 *
 * Si ninguna capa trae el campo (ni el header `Retry-After`), devuelve
 * `DEFAULT_RESEND_COOLDOWN_SECONDS` para no dejar el modal sin cuenta atrás.
 */
export const getResendCooldownSeconds = (error: unknown, fallbackSeconds = DEFAULT_RESEND_COOLDOWN_SECONDS): number => {
  for (const payload of collectPayloads(error)) {
    for (const field of RETRY_AFTER_FIELDS) {
      const seconds = toSeconds(payload[field]);
      if (seconds !== undefined) return seconds;
    }
  }

  return readRetryAfterFromHeaders(error) ?? fallbackSeconds;
};
